import React, { useState } from 'react';
import HeroSection from '../components/shared/HeroSection';
import SectionTitle from '../components/shared/SectionTitle';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../components/ui/carousel';
import {
  Dialog,
  DialogContent,
} from '../components/ui/dialog';
import { heroImages, brands } from '../data/mock';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, X, ChevronDown } from 'lucide-react';
import { useCarousel } from '../hooks/useCarousel';

// Fallback images in case database is empty
const fallbackImages = [
  '/assets/images/chaussures-carousel-1.webp',
  '/assets/images/chaussures-carousel-2.webp',
  '/assets/images/chaussures-carousel-3.webp',
  '/assets/images/chaussures-carousel-4.webp',
  '/assets/images/chaussures-carousel-5.jpg',
  '/assets/images/chaussures-carousel-6.jpg',
  '/assets/images/chaussures-carousel-7.jpg',
  '/assets/images/chaussures-carousel-8.jpg',
];

const ChaussuresPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [expandedBrand, setExpandedBrand] = useState(null);
  const { images: dbImages, loading } = useCarousel('chaussures');

  const toggleBrand = (brandName) => {
    setExpandedBrand(expandedBrand === brandName ? null : brandName);
  };
  
  // Use database images or fallback
  const carouselImages = dbImages.length > 0 ? dbImages : fallbackImages;

  return (
    <div className="min-h-screen">
      {/* Lightbox Modal */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl p-0 bg-transparent border-none">
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-2 right-2 z-10 p-2 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Chaussures de running"
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>

      <HeroSection
        image={heroImages.chaussures}
        title="Les Chaussures"
        subtitle="Plus de 50 références sélectionnées pour tous les profils de coureurs"
        height="h-[50vh]"
      />

      {/* Philosophy Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-neuropolitical font-bold text-gray-900 mb-6">Notre Philosophie</h2>
              <p className="text-gray-600 leading-relaxed mb-4 text-justify">
                Chez nous, aucune marque ni aucun modèle n'a le monopole de la qualité ou de la performance. Chaque coureur est unique, et chaque chaussure répond à un besoin différent.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4 text-justify">
                Venez découvrir plus de 50 références, du minimalisme au maximalisme, disponibles en magasin.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4 text-justify">
                Notre rôle ? Vous orienter vers le modèle le plus adapté à votre gabarit, vos objectifs et la morphologie de vos pieds. Lors de votre venue, nous prendrons le temps d'échanger pour cerner vos besoins réels et vos attentes précises : route ou trail, fréquence d'entraînement, kilométrage moyen, niveau, objectifs personnels… tous ces paramètres sont essentiels pour construire un conseil sur mesure.
              </p>
              <p className="text-gray-600 leading-relaxed text-justify">
                Et surtout : apportez vos anciennes chaussures ! Elles racontent votre histoire de coureur — votre foulée, vos habitudes, vos terrains. Grâce à elles, nous pourrons établir un diagnostic précis et vous aider à faire le bon choix, en toute confiance.
              </p>
            </div>
            <div className="bg-emerald-800 rounded-2xl p-8 text-white">
              <h2 className="text-3xl font-neuropolitical font-bold mb-6">En Pratique</h2>
              <p className="text-emerald-100 leading-relaxed mb-4 text-justify">
                Savez-vous que votre pointure de running doit être supérieure à celle de vos chaussures de ville ?
              </p>
              <p className="text-emerald-100 leading-relaxed mb-4 text-justify">
                En effet, pendant l'effort, vos pieds peuvent gonfler jusqu'à 8 % et glisser légèrement vers l'avant, notamment dans les descentes. C'est pourquoi il est essentiel de garder une marge de sécurité entre votre orteil le plus long et l'avant de la chaussure.
              </p>
              <p className="text-emerald-100 leading-relaxed mb-4 text-justify">
                Chez nous, le choix d'une paire ne se fait ni sur la marque, ni sur le style, mais sur le confort réel et les sensations que vous ressentez, accompagnés des conseils de nos spécialistes.
              </p>
              <p className="text-emerald-100 leading-relaxed text-justify">
                Parce qu'une bonne chaussure, c'est avant tout celle qui respecte vos pieds et votre foulée. Pour le vérifier, notre technologie Footscan unique en France est votre meilleur allié.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Carousel Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {carouselImages.map((image, index) => (
                <CarouselItem key={index} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  <div 
                    className="overflow-hidden rounded-xl shadow-lg cursor-pointer"
                    onClick={() => setSelectedImage(image)}
                  >
                    <img
                      src={image}
                      alt={`Chaussures de running ${index + 1}`}
                      className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="-left-4 lg:-left-12" />
            <CarouselNext className="-right-4 lg:-right-12" />
          </Carousel>
        </div>
      </section>

      {/* Brands Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <SectionTitle
            title="Nos Marques"
            subtitle="Une sélection rigoureuse des meilleures marques du marché"
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-10 items-start">
            {brands.chaussures.map((brand) => (
              <div key={brand.name}>
                {/* Brand Header - Clickable */}
                <button
                  onClick={() => toggleBrand(brand.name)}
                  className="w-full py-4 flex items-center justify-center gap-3"
                  data-testid={`brand-toggle-${brand.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="h-14 w-44 flex items-center justify-center">
                    {brand.logo ? (
                      <img 
                        src={brand.logo} 
                        alt={brand.name} 
                        className="object-contain" 
                        style={{ 
                          maxHeight: `${56 * (brand.logoScale || 1)}px`,
                          maxWidth: `${160 * (brand.logoScale || 1)}px`
                        }}
                        loading="lazy" 
                        decoding="async" 
                      />
                    ) : (
                      <span className="font-bold text-xl text-gray-900">{brand.name}</span>
                    )}
                  </div>
                  <ChevronDown 
                    className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ${
                      expandedBrand === brand.name ? 'rotate-180' : ''
                    }`} 
                  />
                </button>
                
                {/* Models - Expandable */}
                <div 
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    expandedBrand === brand.name ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="pb-4">
                    <p className="text-xs text-gray-500 mb-2">{brand.models.length} modèles</p>
                    <div className="flex flex-wrap gap-1.5">
                      {brand.models.map((model) => (
                        <span
                          key={model}
                          className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-700"
                        >
                          {model}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ChaussuresPage;
